<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard">
        <template v-slot:header>{{ approving ? 'Approve resets' : 'Deny resets' }}</template>
        <template v-slot:subheader>Run #{{ props.run.id }} · {{ props.run.team_name }}</template>

        <div class="flex flex-col gap-2 text-sm text-slate-800">
            <template v-if="approving">
                <strong data-test="decision-question">
                    Reset {{ contactsLabel(props.run.counts?.reassigned_breakdown.not_yet_applied) }} now?
                </strong>
                <p class="m-0" data-test="decision-explanation">
                    Each contact is read again when its reset is applied: one that has opted in or out since the query
                    is left alone, and so is one whose subscriptions or opt-in date attribute have changed in any other
                    way, for Check remaining contacts to check again. The rest of each contact is reset as it stands
                    then. A reset cannot be undone. The run then finishes as Completed, or as Completed with errors.
                </p>
            </template>
            <template v-else>
                <strong data-test="decision-question">Deny every reset of this run?</strong>
                <p class="m-0" data-test="decision-explanation">
                    Nobody is reset, and the run ends as Denied with its results kept. It can then be neither approved
                    nor followed up; checking the team again needs a new run. To stop it but keep its results for Check
                    remaining contacts, cancel it instead.
                </p>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Go back
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-decision"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    :color="approving ? 'primary' : 'danger'"
                >
                    {{ approving ? 'Approve resets' : 'Deny resets' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxModal } from '@voxie/frontend-components';
import { isAxiosError } from 'axios';
import { computed, ref } from 'vue';
import { contactsLabel } from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import type { RndRunApprovalAction } from '~/components/kiosk/kiosk-reassigned-numbers/runs/kioskRndApprovalHelpers';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun } from '~/services/adminRndClient.types';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ run: RndRun; action: RndRunApprovalAction }>();
const emit = defineEmits<{ decided: [run: RndRun]; conflict: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const submitting = ref(false);

const approving = computed(() => props.action === 'approve');

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = approving.value
            ? await adminRndClient.approveRun(props.run.id)
            : await adminRndClient.denyRun(props.run.id);

        emit('decided', data);
        visible.value = false;
    } catch (error) {
        toasted.error(catchRequestError(error), { sticky: true });

        if (isAxiosError(error) && HTTP_CONFLICT === error.response?.status) {
            emit('conflict');
            visible.value = false;
        }
    } finally {
        submitting.value = false;
    }
};
</script>
